// React components
import { React, useState, useEffect, useRef } from "react";

// @mui material components
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import { CircularProgress } from "@mui/material";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Custom styles for the Chatbot
import ChatBotRoot from "components/ChatBot/ChatBotRoot";

// Material Dashboard 2 PRO React context
import { useMaterialUIController, setOpenChatBot, setChatPrefill } from "context";

// Services
import CrudService from "services/cruds-service";
import MDEditor from "@uiw/react-md-editor";
import Stack from "@mui/material/Stack";


function ChatBot({ courseId }) {
  const [controller, dispatch] = useMaterialUIController();
  const { openChatBot, darkMode, chatPrefill } = controller;
  const [disabled, setDisabled] = useState(false);
  const [loader, setLoader] = useState(false);
  const [queryInput, setQueryInput] = useState("");
  // state for animated dots
  const [dots, setDots] = useState(".");

  useEffect(() => {
    let interval;
    if (loader) {
      interval = setInterval(() => {
        setDots((prev) => (prev.length === 3 ? "." : prev + "."));
        // update the last message’s bot_message live
        setChatbotHistory((prev) => {
          const updated = [...prev];
          if (updated.length > 0 && !updated[updated.length - 1].bot_message) {
            updated[updated.length - 1].bot_message = dots;
          } else if (updated.length > 0) {
            updated[updated.length - 1].bot_message = dots;
          }
          return updated;
        });
      }, 500);
    }
    return () => clearInterval(interval);
  }, [loader, dots]);




  const [chatbotHistory, setChatbotHistory] = useState([
    {
      user_message: "",
      bot_message: "Hello, how can I help you today?",
    },
  ]);
  const scrollRef = useRef(null);
  useEffect(() => {
    // wait for DOM to paint the new message, then scroll
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
  }, [chatbotHistory, loader]);


  useEffect(() => {
    if (chatPrefill && openChatBot) {
      setQueryInput("Explain: " + chatPrefill);       // inject into input
      setChatPrefill(dispatch, "");     // clear global prefill after consuming
    }
  }, [chatPrefill, openChatBot, dispatch]);
  // Use the useEffect hook to change the button state for the sidenav type based on window size.
  useEffect(() => {
    // A function that sets the disabled state of the buttons for the sidenav type.
    function handleDisabled() {
      return window.innerWidth > 1200 ? setDisabled(false) : setDisabled(true);
    }

    // The event listener that's calling the handleDisabled function when resizing the window.
    window.addEventListener("resize", handleDisabled);

    // Call the handleDisabled function to set the state with the initial value.
    handleDisabled();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  // Function to close the chatbot
  const handleCloseChatBot = () => setOpenChatBot(dispatch, false);

  // Function to get the chatbot response
  async function getChatbotResponse(payload) {
    const response = await CrudService.getChatbotResponse(payload);
    return response;
  }



  const getResponse = async (message) => {
    if (!message.trim()) return;

    // Add user message + empty bot_message placeholder
    const tempHistory = [
      ...chatbotHistory,
      { user_message: message },
    ];
    setChatbotHistory(tempHistory);
    setQueryInput("");
    setLoader(true);

    const payload = {
      course_id: courseId,
      chatbot_history: JSON.stringify(tempHistory),
      query: message,
    };

    try {
      const response = await getChatbotResponse(payload);
      setChatbotHistory((prev) => {
        const updated = [...prev];
        updated[updated.length - 1].bot_message = response["answer"];
        return updated;
      });
    } finally {
      setLoader(false);
    }
  };



  return (
    <ChatBotRoot variant="permanent" ownerState={{ openChatBot }}>
      <MDBox display="flex" flexDirection="column" sx={{ height: "100vh", overflow: "hidden" }}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="baseline"
          pt={4}
          pb={0.5}
          px={1}
        >
          <MDBox>
            <MDTypography variant="h6">QuCopilot</MDTypography>
          </MDBox>

          <Icon
            sx={({ typography: { size }, palette: { dark, white } }) => ({
              fontSize: `${size.lg} !important`,
              color: darkMode ? white.main : dark.main,
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
              transform: "translateY(5px)",
            })}
            onClick={handleCloseChatBot}
          >
            close
          </Icon>
        </MDBox>

        <Divider />

        <MDTypography
          variant="caption"
          color="text"
          p={1}
          pb={2}
          style={{
            width: "100%",
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          Note: QuCopilot is an experimental AI-bot that utilizes information for
          this particular course. Quantuniversity is not responsible and is not
          liable for any output provided by the QuCopilot. Use QuCopilot at your
          own risk.
        </MDTypography>

        <MDBox pt={0.5} pb={3} px={1} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <Stack
            ref={scrollRef}
            spacing={1.25}
            sx={{ height: "100%", width: "100%", overflowY: "auto", overflowX: "hidden", pb: 1 }}
          >
            {chatbotHistory.map((chat, index) => (
              <MDBox key={index}>
                {/* User message */}
                {!!chat.user_message?.trim() && (
                  <MDBox display="flex" alignItems="flex-end" mb={2}>
                    <MDBox
                      flexGrow={1}
                      mr={1}
                      sx={{ minWidth: 0, maxWidth: "100%", flexBasis: "0%" }}  // ← important
                    >
                      <MDTypography
                        variant="body2"
                        color="secondary"
                        style={{
                          backgroundColor: darkMode ? "#424242" : "#f4f4f4",
                          padding: "10px",
                          borderRadius: "20px 20px 0px 20px",
                          maxWidth: "100%",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        <MDBox pr={2} py={1}>
                          <div data-color-mode="light" style={{ maxWidth: "100%" }}>
                            <MDEditor.Markdown
                              source={chat.user_message}
                              style={{ background: "transparent", maxWidth: "100%" }}
                            />
                          </div>
                        </MDBox>
                      </MDTypography>
                    </MDBox>

                    <MDBox width={32} display="flex" justifyContent="center" alignItems="flex-end">
                      <Icon color="dark">account_circle_outlined</Icon>
                    </MDBox>
                  </MDBox>
                )}

                {/* Bot message */}
                {!!chat.bot_message?.trim() && (
                  <MDBox display="flex" alignItems="flex-end" my={0.5}>
                    <MDBox width={32} display="flex" justifyContent="center" alignItems="flex-end" mr={1}>
                      <Icon color="info">smart_toy</Icon>
                    </MDBox>

                    <MDBox
                      flexGrow={1}
                      sx={{ minWidth: 0, maxWidth: "100%", flexBasis: "0%" }}  // ← important
                    >
                      <MDTypography
                        variant="body2"
                        color="info"
                        style={{
                          backgroundColor: darkMode ? "#424242" : "#f4f4f4",
                          padding: "10px",
                          borderRadius: "20px 20px 20px 0px",
                          maxWidth: "100%",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        <MDBox pr={4} py={2}>
                          <div data-color-mode="light" style={{ maxWidth: "100%" }}>
                            <MDEditor.Markdown
                              source={chat.bot_message}
                              style={{ background: "transparent", maxWidth: "100%" }}
                            />
                          </div>
                        </MDBox>
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                )}
              </MDBox>
            ))}
          </Stack>
        </MDBox>


        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pt={2}
          pb={2}
          px={1}
          sx={{ flexShrink: 0, width: "100%", borderTop: (t) => `1px solid ${t.palette.divider}` }}
        >

          <Divider />
          <MDBox
            width="100%"
            style={{
              backgroundColor: darkMode ? "#424242" : "#f4f4f4",
            }}
          >
            <MDBox display="flex">
              <MDBox width="100%">
                <MDInput
                  type="text"
                  placeholder="Type your message here..."
                  value={queryInput}
                  fullWidth
                  onChange={(e) => setQueryInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && queryInput.trim() !== "") {
                      e.preventDefault();
                      getResponse(queryInput);
                      setQueryInput("");
                    }
                  }}
                />
              </MDBox>
              <MDBox>
                <MDButton
                  variant="contained"
                  color="info"
                  padding={0}
                  style={{
                    marginTop: "2px",
                    marginLeft: "10px",
                  }}
                  onClick={() => {
                    getResponse(queryInput);
                    setQueryInput("");
                  }}
                >
                  <Icon disabled={queryInput == ""}>send_outlined</Icon>
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </ChatBotRoot>
  );
}

export default ChatBot;
